/* eslint-disable max-statements */
import loadScript from '../../utils/loadScript';
import logger from '../../config/app.logger';
import { getExtendedLogger } from '../Logger';
import { getBerryAuthToken, getBerryWidgetStyle } from './utils';
import { isAdminSite } from '../../utils';

import {
  BERRY_APP_WIDGET_URL,
  LOGGER_NAME,
  BERRY_EVENT_ORIGIN,
  BERRY_APP_EVENT_HANDLERS_TYPES,
  ADMIN_BOT_NAME,
  LEARNER_BOT_NAME,
  BERRY_ENABLED_TYPES,
} from './constant';

import { TBerry, TBerryEventHandlers, TBerryOptions } from './types';

const log = getExtendedLogger(LOGGER_NAME, logger);
let berryInitialised: Boolean | null = null;
let handOffData = {};
declare var Berry: TBerry;

export async function loadBerry() {
  await loadScript({
    url: BERRY_APP_WIDGET_URL,
    defer: true,
  });
}

function attachEventHandlers(eventHandlers: TBerryEventHandlers) {
  const { onBerryHandOff, onBerryHandShake } = eventHandlers;

  window.addEventListener('message', event => {
    // Check the origin of the message for security
    if (event.origin !== BERRY_EVENT_ORIGIN) {
      return;
    }
    const message = event.data;

    // Your code to handle the message goes here
    if (message.type === BERRY_APP_EVENT_HANDLERS_TYPES.BERRY_HANDOFF_EVENT) {
      onBerryHandOff?.(event.data);
    }

    if (message.type === BERRY_APP_EVENT_HANDLERS_TYPES.BERRY_WIDGET_HANDSHAKE) {
      berryInitialised = true;
      onBerryHandShake?.();
    }
  });
}

function prepareOptionsFromConfig(config: TBerryOptions) {
  const {
    token,
    primaryColor,
    isOpenByDefault = false,
    customerId,
    berryBotUrlPath,
    toggleIconUrl,
    parentElementId,
    customProps,
  } = config;
  const isAdmin = !!isAdminSite();
  const botUrlPath = berryBotUrlPath || (isAdmin ? ADMIN_BOT_NAME : LEARNER_BOT_NAME);
  const position = isAdmin
    ? { side: 'left', offsetX: 16, offsetY: 16 }
    : { side: 'right', offsetX: 24, offsetY: 24 };

  return {
    token: token,
    position,
    primaryColor,
    botUrlPath,
    isOpenByDefault,
    hideToggle: true,
    showClose: true,
    hideSendIcon: true,
    showResize: true,
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.08)',
    toggleSize: 48,
    headerLeftAligned: true,
    hideChatHistory: false,
    customerId,
    customFont: 'Inter',
    tooltip: 'Chat with support',
    toggleIconUrl,
    customProps,
    parentElementId,
    // notch: {offset: 50},
    // productId: '',        // Specify productId if you have multiple
    // supportOptions:[1,2], // Specify support options
    // height: 704,          // An integer between 604 and 704
    // showNewChat: true,
  } as TBerryOptions;
}

async function loadBerryApp(config: TBerryOptions, eventHandlers: TBerryEventHandlers = {}) {
  const response: { token?: string } = await getBerryAuthToken();

  if (!response?.token) {
    berryInitialised = false;
    log(
      'Token not present, skipping loading phase. Berry will not be loaded due to token not present.'
    );
    return;
  }
  const options = prepareOptionsFromConfig({ ...config, token: response?.token });

  await loadBerry();

  if (!Berry) {
    log('not loaded, skipping initialisation phase');
    berryInitialised = false;
    return;
  }
  log('initialising with options %o', options);
  Berry?.init(options);
  berryInitialised = true;

  attachEventHandlers(eventHandlers);
}

const showBerry = () => {
  Berry?.show();
};

const hideBerry = () => {
  Berry?.hide();
};

const isBerryInitialized = () => berryInitialised;

const toggleBerryIcon = ({ showIcon }: { showIcon: boolean }) => {
  Berry?.setToggleVisibility(showIcon);
};

const toggleBerrychat = ({
  hideButton,
  hideChatbox = true,
}: {
  hideButton: boolean;
  hideChatbox: boolean;
}) => {
  try {
    toggleBerryIcon({ showIcon: !hideButton });
    hideChatbox ? hideBerry() : showBerry();
  } catch (error) {
    console.error(error); //eslint-disable-line no-console
  }
};

// This function is used in learner-header for updating the sendFeedbackFromBerry function
const updateSendFeedbackFromBerry = (callback?: (data: any) => boolean) => {
  log('sendFeedbackFromBerry function updated with get support function.');
  if (isBerryInitialized() && callback) {
    Berry.sendFeedbackFromBerry = callback;
  }
};

let sendFeedbackFromBerry = async (data: any) => {
  if (Berry?.sendFeedbackFromBerry) {
    const res = await Berry?.sendFeedbackFromBerry(data);
    return res;
  }

  log(
    'Could not call sendfeedback from getsupport as it was not updated using function updateSendFeedbackFromBerry'
  );
};

const updateBerryHandoffData = (data: any) => {
  if (isBerryInitialized()) {
    handOffData = data;
  } else {
    // eslint-disable-next-line no-unused-vars
    handOffData = {};
  }
};

const getBerryHandoffData = () => handOffData || {};

const isBerryEnabled = (enabledType?: string) =>
  enabledType && Object.values(BERRY_ENABLED_TYPES).includes(enabledType);

const isBerryEnabledForAdmin = (enabledType?: string) =>
  BERRY_ENABLED_TYPES.ADMIN === enabledType || BERRY_ENABLED_TYPES.BOTH === enabledType;

const isBerryEnabledForLearner = (enabledType?: string) =>
  BERRY_ENABLED_TYPES.LEARNER === enabledType || BERRY_ENABLED_TYPES.BOTH === enabledType;

const updateProperties = (customProperties: object) => {
  if (isBerryInitialized()) {
    Berry?.update({ customProps: customProperties });
  }
};

export {
  loadBerryApp,
  showBerry,
  hideBerry,
  isBerryInitialized,
  toggleBerryIcon,
  toggleBerrychat,
  getBerryWidgetStyle,
  sendFeedbackFromBerry,
  updateSendFeedbackFromBerry,
  isBerryEnabled,
  isBerryEnabledForAdmin,
  isBerryEnabledForLearner,
  updateBerryHandoffData,
  getBerryHandoffData,
  updateProperties,
};
